import { useEffect } from 'react';
import { TrackingEvent } from '../../AnalyticsContext/TrackingEvent';
import { useAnalytics } from '../../AnalyticsContext/useAnalytics';
import { AsyncResult } from '../../types/AsyncResult';
import { useAsyncCacheEntry } from '../../util/AsyncCache/AsyncCache';
import { useServices } from '../Services';
import { useFlags } from '../flags';
import { FlagrData, FlagrEntity } from './types';

// https://flagr.healthengine.com.au/#/flags/23
export const NEW_PRACTICE_PROFILE_FLAGR = 'k493xcsb6re868ih4-new-practice-profiles';

// https://flagr.healthengine.com.au/#/flags/74
export const NEW_PRACTITIONER_PROFILE_FLAGR = 'kq4kdhskpwuekimau-new-practitioner-profiles';

// https://flagr.healthengine.com.au/#/flags/54
export const APPOINTMENT_PICKER_PAGE = 'kv7k4452ms49u7tju';

// https://flagr.healthengine.com.au/#/flags/24
export const COVAX_LIGHTBOX = 'kfvkua3hvbq1ikm4o-covax-lightbox';

// https://flagr.healthengine.com.au/#/flags/49
export const PHI_HOMEPAGE_FLAG = 'kxpf477279nskco3j';

// https://flagr.healthengine.com.au/#/flags/45
export const PHI_PARTNER_FEATURES = 'kdq93jy6dcsnte2yq';

// https://flagr.healthengine.com.au/#/flags/46
export const TELEHEALTH_LANDING_PAGE_VARIANT = 'k4srwe14uop2jfefi';

// https://flagr.healthengine.com.au/#/flags/61
export const PRIVACY_SETTINGS_CENTRE = 'kyqg1ju2yt2x4ejee';

/**
 * Evaluates a given flag using the FlagrService.
 * @returns an AsyncResult of either a FlagrVariant or null (if no variant matched or if the flag is not enabled).
 */
export const useFlagr = (flagKey: string, entity?: FlagrEntity): AsyncResult<FlagrData> => {
  const { flagr } = useServices();

  const cacheEntry = flagr.evaluate(flagKey, entity);
  const result = useAsyncCacheEntry(cacheEntry);

  const variant = result.data?.key;

  const { trackEvent } = useAnalytics(
    variant === undefined ? undefined : { flagr: { [flagKey]: variant } },
  );

  useEffect(() => {
    /** We assume that the null variant is always the control, so no event is needed. */
    if (variant === undefined) return;
    if (flagr.analyticsLoggedKeys.has(flagKey)) return;

    flagr.analyticsLoggedKeys.add(flagKey);

    trackEvent(TrackingEvent.FLAGR_TRIGGERED);
  }, [flagKey, flagr.analyticsLoggedKeys, trackEvent, variant]);

  return result;
};

/**
 * Evaluates a simple on/off flag using the FlagrService.
 * @returns an AsyncResult of a boolean, which will be true if ANY variant matched, or false if NONE did.
 */
export const useSimpleFlagr = (flagKey: string, entity?: FlagrEntity): AsyncResult<boolean> => {
  const { loading, data, error } = useFlagr(flagKey, entity);

  /** If a URL flag is provided which matches the this flagr key, we override to true. */
  const urlFlags = useFlags();
  if (urlFlags[flagKey] !== undefined) return { loading: false, data: urlFlags[flagKey] };

  if (loading || error) return { loading, error };
  return { loading: false, data: data !== null };
};
